import supabase from '../lib/supabase';

/**
 * Service for handling database operations with Supabase (Multi-Department)
 * CLEANED: All schedule persistence methods removed
 */
export default class DatabaseService {
  // Helper Functions (kept for other operations)
  
  /**
   * Get current department ID with fallback strategy
   */
  static getCurrentDepartmentId() {
    const localStorageId = localStorage.getItem('currentDepartmentId');
    if (localStorageId) {
      console.log(`DatabaseService: Found department ID in localStorage: ${localStorageId}`);
      return localStorageId;
    }
    
    if (typeof window !== 'undefined' && window.__CURRENT_DEPARTMENT_ID__) {
      const windowId = window.__CURRENT_DEPARTMENT_ID__;
      localStorage.setItem('currentDepartmentId', windowId);
      return windowId;
    }
    
    console.error('DatabaseService: No department ID found in localStorage or fallback sources');
    throw new Error('No department selected. Please select a department first.');
  }

  /**
   * Safe method to get department ID
   */
  static getSafeDepartmentId(departmentId = null) {
    if (departmentId) {
      const storedId = localStorage.getItem('currentDepartmentId');
      if (storedId !== departmentId) {
        localStorage.setItem('currentDepartmentId', departmentId);
      }
      return departmentId;
    }
    
    try {
      return this.getCurrentDepartmentId();
    } catch (error) {
      const lastResortId = this.tryLastResortDepartmentId();
      if (lastResortId) {
        localStorage.setItem('currentDepartmentId', lastResortId);
        return lastResortId;
      }
      return null;
    }
  }

  /**
   * Last resort method to find department ID
   */
  static tryLastResortDepartmentId() {
    try {
      const sessionKeys = Object.keys(sessionStorage);
      for (const key of sessionKeys) {
        if (key.includes('department') || key.includes('dept')) {
          try {
            const data = JSON.parse(sessionStorage.getItem(key));
            if (data && data.id) {
              return data.id;
            }
          } catch (e) {
            // Include comment to satisfy no-empty lint rule
            /* ignore parsing errors */
          }
        }
      }
      
      if (typeof window !== 'undefined' && window.location) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlDeptId = urlParams.get('departmentId') || urlParams.get('dept');
        if (urlDeptId) {
          return urlDeptId;
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Validate department access
   */
  static async validateDepartmentAccess(departmentId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      
      const { data, error } = await supabase
        .from('user_departments_helm')
        .select('department_id')
        .eq('user_id', user.id)
        .eq('department_id', departmentId)
        .single();
      
      return !error && !!data;
    } catch (error) {
      return false;
    }
  }

  /**
   * Save employee
   */
  static async saveEmployee(employee, departmentId = null) {
    try {
      const deptId = this.getSafeDepartmentId(departmentId);
      if (!deptId) {
        throw new Error('Department ID is required to save employee.');
      }
      
      const hasAccess = await this.validateDepartmentAccess(deptId);
      if (!hasAccess) {
        throw new Error('Access denied to this department');
      }
      
      const employeeData = {
        department_id: deptId,
        name: employee.name,
        workload: employee.workload,
        skills: employee.skills,
        preferences: employee.preferences,
        max_consecutive_days: employee.maxConsecutiveDays,
        sick_leave: employee.sickLeave,
        vacation: employee.vacation,
        available_days: employee.availableDays,
      };

      if (employee.id && !employee.id.toString().startsWith('local_')) {
        const { data, error } = await supabase
          .from('employees_helm')
          .update(employeeData)
          .eq('id', employee.id)
          .eq('department_id', deptId)
          .select();
        
        if (error) throw error;
        return data[0] ? transformEmployeeFromDb(data[0]) : employee;
      } else {
        const { data, error } = await supabase
          .from('employees_helm')
          .insert(employeeData)
          .select();
        
        if (error) throw error;
        return data[0] ? transformEmployeeFromDb(data[0]) : employee;
      }
    } catch (error) {
      if (!employee.id) {
        employee.id = `local_${Date.now()}`;
      }
      return employee;
    }
  }

  /**
   * Delete employee
   */
  static async deleteEmployee(employeeId, departmentId = null) {
    try {
      const deptId = this.getSafeDepartmentId(departmentId);
      if (!deptId) {
        return false;
      }
      
      if (!employeeId.toString().startsWith('local_')) {
        const { error } = await supabase
          .from('employees_helm')
          .delete()
          .eq('id', employeeId)
          .eq('department_id', deptId);
        
        if (error) throw error;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Load employees
   */
  static async loadEmployees(departmentId = null) {
    try {
      const deptId = this.getSafeDepartmentId(departmentId);
      if (!deptId) {
        return [];
      }
      
      const { data, error } = await supabase
        .from('employees_helm')
        .select('*')
        .eq('department_id', deptId)
        .order('name');
      
      if (error) throw error;
      return data.map(transformEmployeeFromDb);
    } catch (error) {
      return [];
    }
  }

  /**
   * Save settings
   */
  static async saveSettings(settings, departmentId = null) {
    try {
      const deptId = this.getSafeDepartmentId(departmentId);
      if (!deptId) {
        throw new Error('Department ID is required to save settings.');
      }
      
      const hasAccess = await this.validateDepartmentAccess(deptId);
      if (!hasAccess) {
        throw new Error('Access denied to this department');
      }
      
      const settingsData = {
        department_id: deptId,
        shifts: settings.shifts,
        min_staffing: settings.minStaffing,
        rules: settings.rules,
        holidays: settings.holidays,
        weekend_rules: {
          under50: settings.weekendRules?.under50 || 1,
          over50: settings.weekendRules?.over50 || 2,
        },
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('settings_helm')
        .upsert(settingsData, {
          onConflict: 'department_id',
          ignoreDuplicates: false
        })
        .select();
      
      if (error) throw error;
      return data[0] ? transformSettingsFromDb(data[0]) : settings;
    } catch (error) {
      return settings;
    }
  }

  /**
   * Load settings
   */
  static async loadSettings(departmentId = null) {
    try {
      const deptId = this.getSafeDepartmentId(departmentId);
      if (!deptId) {
        return null;
      }
      
      const hasAccess = await this.validateDepartmentAccess(deptId);
      if (!hasAccess) {
        return null;
      }
      
      const { data, error } = await supabase
        .from('settings_helm')
        .select('*')
        .eq('department_id', deptId)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return transformSettingsFromDb(data);
    } catch (error) {
      return null;
    }
  }
}

// Transform helpers
function transformEmployeeFromDb(dbEmployee) {
  return {
    id: dbEmployee.id,
    name: dbEmployee.name,
    workload: dbEmployee.workload,
    skills: dbEmployee.skills || [],
    preferences: dbEmployee.preferences || [],
    maxConsecutiveDays: dbEmployee.max_consecutive_days || 4,
    sickLeave: dbEmployee.sick_leave || { from: '', to: '' },
    vacation: dbEmployee.vacation || { from: '', to: '' },
    availableDays: dbEmployee.available_days || {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    },
  };
}

function transformSettingsFromDb(dbSettings) {
  return {
    shifts: dbSettings.shifts || {
      früh: { start: '07:00', end: '15:54', hours: 8, minutes: 24 },
      zwischen: { start: '10:00', end: '18:54', hours: 8, minutes: 24 },
      spät: { start: '12:45', end: '21:39', hours: 8, minutes: 54 },
    },
    minStaffing: dbSettings.min_staffing || {
      weekday: { früh: 2, zwischen: 3, spät: 2 },
      weekend: { früh: 1, zwischen: 2, spät: 1 },
    },
    rules: dbSettings.rules || {
      minRestHours: 11,
      noEarlyAfterLate: true,
      maxHoursPerDay: 12,
      maxHoursPerWeek: 48,
      hoursTolerance: 8,
      minutesTolerance: 0,
      minDaysOffBetweenBlocks: 2,
    },
    holidays: dbSettings.holidays || [],
    weekendRules: dbSettings.weekend_rules || { under50: 1, over50: 2 },
    webhooks: { schedule: '', employees: '' },
  };
}